import Button from '@material-ui/core/Button';
import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogContentText from '@material-ui/core/DialogContentText';
import DialogTitle from '@material-ui/core/DialogTitle';
import type { Theme } from '@material-ui/core/styles';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import * as React from 'react';
import { useObservable } from 'react-use-observable';
import analyticsService from 'v2/services/analytics';
import eduzzAccountsService from 'v2/services/eduzzAccount';
import { settingsFactory } from 'v2/services/settings';
import { uiManagerService } from 'v2/services/uiManager';

import type { ContentNew } from 'v2/interfaces/settings';

import useStyles from './styles';
import NoDangerouslyHTML from '../NoDangerouslyHTML';

const LOCAL_STORAGE_KEY = 'modal_news_seen';

export const ModalNews = React.memo(() => {
  const classes = useStyles();
  const isMobile = useMediaQuery((theme: Theme) => theme.breakpoints.down('xs'));

  const [loadingTours] = useObservable(() => eduzzAccountsService.getLoadingTours(), []);
  const [currentTour] = useObservable(() => uiManagerService.getCurrentlyTour(), []);
  const [currentStep, setCurrentStep] = React.useState(1);

  const [news, setNews] = React.useState<ContentNew[]>([]);
  const [open, setOpen] = React.useState(false);

  const [contentNews] = useObservable(() => settingsFactory.getNews(), []);
  const [alreadyTrackedSeenNews, setAlreadyTrackedSeenNews] = React.useState<string[]>([]);
  const [shouldTrackSeenNews, setShouldTrackSeenNews] = React.useState<ContentNew>();

  const saveNewsToLocalStorage = (news: string[]) => {
    localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(news));
  };

  const handleNext = () => {
    setCurrentStep((prev) => prev + 1);
  };

  const handlePrev = () => {
    setCurrentStep((prev) => prev - 1);
  };

  const handleFinish = () => {
    const updatedStoragedKeys = news.map((modal) => modal.id);
    saveNewsToLocalStorage(updatedStoragedKeys);
    setOpen(false);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const handleClickCTA = (e: Event, el: Element) => {
    const title = el.attributes.getNamedItem('data-title').value;
    const link = el.attributes.getNamedItem('data-link').value;
    if (!title || !link) return;

    e.preventDefault();

    analyticsService.sendEvent(`${title} - Clicked CTA`, `${title} - Modal News`);

    const target = el.attributes.getNamedItem('data-target').value;
    window.open(link, target);
  };

  React.useEffect(() => {
    if (!contentNews) return;

    const storagedKeys = JSON.parse(localStorage.getItem(LOCAL_STORAGE_KEY)) || [];
    const notSeenContents = contentNews.filter((content) => !storagedKeys.includes(content.id));
    const shouldShowNews = notSeenContents.length && loadingTours !== null && !loadingTours && !currentTour;

    if (shouldShowNews) {
      setNews(notSeenContents);
      setOpen(true);
    }
  }, [contentNews, currentTour, loadingTours]);

  const isFirstStep = currentStep === 1;
  const isLastStep = currentStep === news.length;

  const currentNews = news[currentStep - 1];

  React.useEffect(() => {
    if (!shouldTrackSeenNews) return;

    const alreadyTrackedNews = alreadyTrackedSeenNews.includes(shouldTrackSeenNews.id);

    if (alreadyTrackedNews) return;

    analyticsService.sendEvent(`${shouldTrackSeenNews.title} - Modal News Viewed`, `${shouldTrackSeenNews.title} - Modal News`);
  }, [shouldTrackSeenNews, alreadyTrackedSeenNews]);

  React.useEffect(() => {
    if (!shouldTrackSeenNews) return;
    setAlreadyTrackedSeenNews((prev) => [...prev, shouldTrackSeenNews.id]);
  }, [shouldTrackSeenNews]);

  const elemRef = React.useCallback(
    (node) => {
      if (node !== null) {
        const cta = window.document.querySelector('#modal-news-cta');
        const title = cta?.attributes.getNamedItem('data-title').value;
        setShouldTrackSeenNews(!!cta ? { ...currentNews, title } : null);
        if (!cta) return;
        cta.addEventListener('click', (e) => handleClickCTA(e, cta));
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [currentNews]
  );

  if (!currentNews) return null;

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullScreen={isMobile}
      scroll='paper'
      classes={{
        paper: classes.paper,
      }}
      disableBackdropClick
    >
      <DialogTitle>{currentNews.title}</DialogTitle>
      <DialogContent className={classes.content} dividers>
        {currentNews.imageUrl && <img className={classes.image} src={currentNews.imageUrl} alt={currentNews.title} />}
        <DialogContentText ref={elemRef} className={classes.contentText} tabIndex={-1}>
          <NoDangerouslyHTML html={currentNews.description} />
        </DialogContentText>
      </DialogContent>
      <DialogActions className={classes.actions}>
        <Button variant='text' onClick={handleClose}>
          Ver depois
        </Button>
        <div>
          {!isFirstStep && (
            <Button variant='text' onClick={handlePrev} color='primary'>
              Anterior
            </Button>
          )}
          {!isLastStep && (
            <Button variant='contained' onClick={handleNext} color='primary'>
              Próximo
            </Button>
          )}
          {isLastStep && (
            <Button variant='contained' onClick={handleFinish} color='primary'>
              Entendi
            </Button>
          )}
        </div>
      </DialogActions>
    </Dialog>
  );
});
